"use client";

import { ArrowLeft, ArrowRight, Calendar, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  PostsProvider,
  usePosts,
} from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-12 w-3/4 bg-muted/30 animate-pulse" />
        <div className="h-96 w-full bg-muted/30 animate-pulse" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/30 animate-pulse" />
          <div className="h-4 w-full bg-muted/30 animate-pulse" />
          <div className="h-4 w-2/3 bg-muted/30 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-muted-foreground">
        Failed to load article.
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="text-muted-foreground">Article not found.</div>
    );
  }

  const authorName = post.author?.name || "B.Y.E. Team";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"];
  const dateFormatted = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-block px-3 py-1 text-xs font-default uppercase tracking-wider bg-primary text-primary-foreground rounded-md"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1
        className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-8 text-foreground"
        dangerouslySetInnerHTML={{ __html: post.title }}
      />

      {}
      <div className="flex flex-wrap items-center gap-6 mb-10 pb-10 border-b border-border">
        <div className="flex items-center gap-3">
          <Avatar className="size-10 rounded-lg">
            {authorAvatar && (
              <AvatarImage src={authorAvatar} alt={authorName} />
            )}
            <AvatarFallback className="rounded-lg bg-secondary text-secondary-foreground font-default text-sm">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-xs font-default uppercase tracking-wider text-muted-foreground">
              Written by
            </span>
            <span className="text-sm font-medium text-foreground">
              {authorName}
            </span>
          </div>
        </div>

        {dateFormatted && (
          <div className="flex items-center gap-3">
            <div className="size-10 flex items-center justify-center bg-accent rounded-lg">
              <Calendar className="size-4 text-accent-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-default uppercase tracking-wider text-muted-foreground">
                Published
              </span>
              <span className="text-sm font-medium text-foreground">
                {dateFormatted}
              </span>
            </div>
          </div>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="mb-12 shadow-lg rounded-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover rounded-lg"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {}
      {post.excerpt && (
        <div
          className="font-default text-xl md:text-2xl leading-relaxed text-foreground/90 mb-10 pl-6 border-l-2 border-primary"
          dangerouslySetInnerHTML={{ __html: post.excerpt }}
        />
      )}

      {}
      {post.content && (
        <div
          className="font-default text-lg leading-relaxed text-foreground/90 space-y-6 [&_p]:mb-6 [&_h2]:font-default [&_h2]:font-bold [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:tracking-tight [&_h3]:font-default [&_h3]:font-bold [&_h3]:text-2xl [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:tracking-tight [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_blockquote]:border-l-2 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:my-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-6 [&_li]:mb-2 [&_img]:my-8 [&_img]:w-full [&_code]:font-mono [&_code]:text-sm [&_code]:bg-muted [&_code]:px-2 [&_code]:py-1"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border">
          <span className="text-xs font-default uppercase tracking-wider text-muted-foreground block mb-4">
            Tagged with
          </span>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span
                data-index={i}
                key={tag.id}
                className="inline-block px-3 py-1 text-xs font-default uppercase tracking-wider border border-border text-foreground rounded-md"
              >
                #{tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {}
      <Card className="mt-16 bg-card/60 backdrop-blur-sm border border-border shadow-md rounded-lg">
        <CardContent className="flex flex-col sm:flex-row gap-6 items-start sm:items-center p-6">
          <Avatar className="size-20 rounded-lg shrink-0">
            {authorAvatar && (
              <AvatarImage src={authorAvatar} alt={authorName} />
            )}
            <AvatarFallback className="rounded-lg bg-primary text-primary-foreground font-default font-bold text-2xl">
              {authorName.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <span className="text-xs font-default uppercase tracking-wider text-muted-foreground block mb-2">
              About the Author
            </span>
            <h3 className="font-default font-bold text-2xl text-foreground mb-2 tracking-tight">
              {authorName}
            </h3>
            <p className="font-default text-base text-muted-foreground leading-relaxed">
              Part of the B.Y.E. Hygiene Mist™ team, dedicated to making family hygiene simple, portable, and gentle for every stage of life.
            </p>
            {post.author?.link && (
              <Link
                to={post.author.link}
                className="inline-flex items-center gap-2 mt-4 text-sm font-default uppercase tracking-wider text-primary"
              >
                <User className="size-3" />
                View all posts
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </article>
  );
}

function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div
            data-index={i}
            key={i}
            className="h-80 bg-muted/30 animate-pulse rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <p className="text-muted-foreground font-default">
        No related articles available.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {posts.slice(0, 3).map((post, i) => (
        <Link
          data-index={i}
          key={post.id}
          to={post.link || "#"}
          className="group"
        >
          <Card className="h-full bg-card/60 backdrop-blur-sm border border-border shadow-md rounded-lg transition-transform hover:-translate-y-1 overflow-hidden">
            {post.featuredImage && (
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            )}
            <CardContent className="p-6">
              {post.categories && post.categories[0] && (
                <span className="inline-block text-xs font-default uppercase tracking-wider text-primary mb-3">
                  {post.categories[0].name}
                </span>
              )}
              <h3
                className="font-default font-bold text-xl text-foreground tracking-tight mb-3 leading-tight"
                dangerouslySetInnerHTML={{ __html: post.title }}
              />
              <div className="inline-flex items-center gap-2 text-sm font-default uppercase tracking-wider text-foreground/80 group-hover:text-primary transition-colors">
                Read more
                <ArrowRight className="size-3" />
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

export default function Post() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section data-section-id="3824"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8"
    >
      <div className="max-w-7xl mx-auto">
        {}
        <div className="max-w-4xl mx-auto mb-10">
          <Link
            to="/Blog"
            className="inline-flex items-center gap-2 text-xs font-default uppercase tracking-wider text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="size-3" />
            Back to Blog
          </Link>
        </div>

        {}
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-7xl mx-auto mt-24 md:mt-32">
          <Separator className="mb-16 bg-border" />

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="inline-block text-xs font-default uppercase tracking-wider text-primary mb-3">
                Continue reading
              </span>
              <h2 className="font-default font-bold text-3xl md:text-5xl tracking-tight text-foreground">
                Related Articles
              </h2>
            </div>
            <Link to="/Blog">
              <Button
                variant="outline"
                className="font-default uppercase tracking-wider text-xs border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary rounded-lg"
              >
                View all
                <ArrowRight className="size-3" />
              </Button>
            </Link>
          </div>

          <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
            <RelatedPostsList />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}